import { useNavigate } from 'react-router';
import { Center, Column, CustomSpinner, Icon, LineClamp, Row, Text } from '~/commonComponents';
import { IPendingReview } from '~/domain/ShiftReview';
import { UserProfession } from '~/enums/UserProfession';
import { Colors } from '~/themes/colors';
import { TableHeader } from './TableHeader';
import { useMediaQuery } from '~/hooks';

interface IProps {
  data: IPendingReview[];
  isLoading?: boolean;
}

export const Table = ({ data = [], isLoading = false }: IProps) => {
  const navigate = useNavigate();
  const [isSmallScreen] = useMediaQuery(['lg']);

  const handleRowClick = (item: IPendingReview) => {
    if (item.hasAdjustment) {
      navigate(`/dashboard/adjustments/${item?.id}`, {
        state: { job_id: item.id },
      });
      return;
    }

    navigate(`/dashboard/job/${item.id}/details`);
  };

  return (
    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
      <TableHeader />
      <tbody>
        {isLoading && (
          <tr>
            <td colSpan={6}>
              <Center>
                <CustomSpinner isLoading />
              </Center>
            </td>
          </tr>
        )}

        {!isLoading &&
          data.map((it) => (
            <tr
              key={it.id}
              style={{ borderBottom: `1px solid ${Colors.neutral_divider}` }}
              onClick={() => handleRowClick(it)}
            >
              <td style={{ paddingTop: '14px', paddingBottom: '8px' }}>
                <img
                  src={it.professional.image}
                  style={{
                    border: `1px solid ${Colors.neutral_100}`,
                    borderRadius: 60,
                    height: 60,
                    objectFit: 'contain',
                    width: 60,
                  }}
                  alt="professional image"
                />
              </td>
              <td style={{ paddingTop: '14px', paddingBottom: '8px' }}>
                <Column>
                  <Text bold fontSize={18}>
                    {it.professional.fullName}
                  </Text>
                  <Text color={Colors.neutral_500}>{UserProfession.getName(it.profession)}</Text>
                </Column>
              </td>
              {!isSmallScreen && (
                <td style={{ paddingTop: '14px', paddingBottom: '8px', width: '25%' }}>
                  <LineClamp
                    lineClamp={2}
                    text={it.offerOwner.officeName}
                    style={{ color: Colors.neutral_500 }}
                  />
                </td>
              )}
              <td style={{ paddingTop: '14px', paddingBottom: '8px' }}>
                <Column>
                  <Text bold>{it.shift.localDate}</Text>
                  <Column>
                    <Text
                      color={Colors.neutral_500}
                      textDecorationLine={it.hasAdjustShiftTime ? 'line-through' : 'none'}
                    >
                      {it.shift.localStartTime} - {it.shift.localEndTime}
                    </Text>
                    {it.hasAdjustShiftTime && (
                      <Text color={Colors.neutral_500}>
                        {it.adjustment?.localStartTime} - {it.adjustment?.localEndTime}
                      </Text>
                    )}
                  </Column>
                  <Column>
                    <Text
                      color={Colors.neutral_500}
                      textDecorationLine={it.hasAdjustBreakTime ? 'line-through' : 'none'}
                    >
                      Meal Break: {it.shift.breakTime}
                    </Text>
                    {it.hasAdjustBreakTime && (
                      <Text color={Colors.neutral_500}>Meal Break: {it.adjustment?.breakTime}</Text>
                    )}
                  </Column>
                </Column>
              </td>
              <td style={{ paddingTop: '14px', paddingBottom: '8px' }}>
                {!it.hasAdjustment ? (
                  <Text bold>Confirm Hours</Text>
                ) : (
                  <Row alignItems="center" gap={8}>
                    <Icon color={Colors.warning_500} name="warning-outline" size={24} />
                    <Text bold>Review Adjustment</Text>
                  </Row>
                )}
              </td>
              <td>
                <Icon name="arrow-right" size={24} />
              </td>
            </tr>
          ))}
      </tbody>
    </table>
  );
};
